import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "revgrow.chat.session";
const HISTORY_KEY = "revgrow.chat.history";
const LEAD_KEY = "revgrow.chat.lead";

// Small helper — persistent per-browser session id.
function loadSession() {
  try {
    let s = window.localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch (_) {
    return `s-${Math.random().toString(36).slice(2, 12)}`;
  }
}

function loadHistory() {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function loadLead() {
  try {
    const raw = window.localStorage.getItem(LEAD_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

const GREETING = {
  role: "assistant",
  content:
    "Hi, I'm the RevGrow assistant. Ask me anything about our work with B2B revenue teams — from ABM programmes to GTM strategy. What brings you in today?",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadHistory() || [GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [lead, setLead] = useState(() => loadLead());
  const [leadForm, setLeadForm] = useState({ name: "", email: "" });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const sessionId = useMemo(loadSession, []);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Persist conversation across reloads
  useEffect(() => {
    try {
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(messages));
    } catch (_) {
      /* ignore */
    }
  }, [messages]);

  // Auto-scroll on new message
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]);

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: trimmed,
      });
      const reply = res.data?.reply || "Sorry, I didn't catch that.";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Something's off on my end. Please try again in a moment, or drop your details below and a partner will get back to you.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (leadSubmitting) return;
    const name = leadForm.name.trim();
    const email = leadForm.email.trim();
    if (!name || !email) return;
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/chat/handoff`, {
        session_id: sessionId,
        name,
        email,
        notes: null,
      });
      const captured = { name, email, at: Date.now() };
      setLead(captured);
      window.localStorage.setItem(LEAD_KEY, JSON.stringify(captured));
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `Thanks ${name.split(" ")[0]} — a partner will pick this up within one working day. In the meantime, happy to keep answering questions here.`,
        },
      ]);
      toast.success("Details shared — a partner will be in touch.");
    } catch (err) {
      toast.error("Couldn't send your details. Please try again.");
    } finally {
      setLeadSubmitting(false);
    }
  };

  const reset = () => {
    setMessages([GREETING]);
    try {
      window.localStorage.removeItem(HISTORY_KEY);
    } catch (_) {}
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Talk to RevGrow"}
        data-testid="chat-launcher"
        className={`fixed z-[70] bottom-6 right-6 group flex items-center gap-3 rounded-full border transition-all duration-500 ${
          open
            ? "border-white/15 bg-ink-800/80 backdrop-blur-xl px-4 py-3"
            : "border-clay-400/40 bg-clay-500 hover:bg-clay-400 px-5 py-3.5 shadow-[0_12px_40px_-8px_rgba(192,94,76,0.55)]"
        }`}
      >
        {open ? (
          <>
            <X size={16} className="text-bone-100" />
            <span className="text-[11px] uppercase tracking-widest2 text-bone-100/80">
              Close
            </span>
          </>
        ) : (
          <>
            <MessageCircle size={16} className="text-bone-100" />
            <span className="text-[12px] uppercase tracking-widest2 font-semibold text-bone-100">
              Talk to RevGrow
            </span>
          </>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed z-[65] bottom-24 right-4 md:right-6 w-[calc(100vw-2rem)] md:w-[420px] max-w-[440px] h-[min(640px,calc(100vh-8rem))] flex flex-col rounded-lg border border-white/10 bg-ink-800/95 backdrop-blur-xl shadow-2xl animate-fade-up overflow-hidden"
          role="dialog"
          aria-label="RevGrow chat"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="flex items-start gap-4 p-5 border-b border-white/10 bg-ink-900/40">
            <div className="w-10 h-10 rounded-full bg-clay-500/20 border border-clay-400/40 flex items-center justify-center shrink-0">
              <span className="font-display text-clay-400 text-lg">R</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-bone-100 font-display text-lg leading-tight">
                RevGrow assistant
              </div>
              <div className="text-[10px] uppercase tracking-widest2 text-bone-200/50 mt-1 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Usually replies instantly · Berlin
              </div>
            </div>
            <button
              onClick={reset}
              className="text-[10px] uppercase tracking-widest2 text-bone-200/40 hover:text-clay-400 transition-colors"
              data-testid="chat-reset"
            >
              Reset
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chat-messages"
            className="flex-1 overflow-y-auto px-5 py-6 space-y-4"
          >
            {messages.map((m, i) => (
              <Message key={i} role={m.role} content={m.content} />
            ))}
            {sending && <TypingBubble />}
          </div>

          {/* Lead form */}
          <div className="border-t border-white/10 bg-ink-900/50 px-5 py-4">
            {lead ? (
              <div
                className="flex items-center gap-3 text-xs text-bone-200/70"
                data-testid="chat-lead-confirmed"
              >
                <Check size={14} className="text-emerald-400 shrink-0" />
                <span>
                  <span className="text-bone-100">{lead.name}</span> · a partner will follow up at{" "}
                  <span className="text-clay-400">{lead.email}</span>.
                </span>
              </div>
            ) : (
              <form
                onSubmit={submitLead}
                className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2"
                data-testid="chat-lead-form"
              >
                <input
                  type="text"
                  placeholder="Name"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                  required
                  data-testid="chat-lead-name"
                  className="bg-transparent border border-white/15 rounded-md px-3 py-2 text-sm text-bone-100 placeholder:text-bone-200/40 focus:outline-none focus:border-clay-400 transition-colors"
                />
                <input
                  type="email"
                  placeholder="Work email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                  required
                  data-testid="chat-lead-email"
                  className="bg-transparent border border-white/15 rounded-md px-3 py-2 text-sm text-bone-100 placeholder:text-bone-200/40 focus:outline-none focus:border-clay-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={leadSubmitting}
                  data-testid="chat-lead-submit"
                  className="inline-flex items-center gap-1 rounded-md bg-clay-500 hover:bg-clay-400 disabled:opacity-60 px-3 py-2 text-[11px] uppercase tracking-widest2 font-semibold text-bone-100 transition-colors"
                >
                  {leadSubmitting ? "Sending" : "Send"}
                  <ArrowRight size={12} />
                </button>
              </form>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="border-t border-white/10 p-3 flex items-center gap-2 bg-ink-900/60"
            data-testid="chat-input-form"
          >
            <input
              ref={inputRef}
              type="text"
              placeholder="Ask about ABM, GTM strategy, timelines…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={sending}
              data-testid="chat-input"
              className="flex-1 bg-transparent px-3 py-2.5 text-sm text-bone-100 placeholder:text-bone-200/30 focus:outline-none"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              aria-label="Send message"
              data-testid="chat-send"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-clay-500 hover:bg-clay-400 disabled:opacity-40 disabled:cursor-not-allowed text-bone-100 transition-colors"
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function Message({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`chat-message-${role}`}
    >
      <div
        className={`max-w-[85%] rounded-lg px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-clay-500 text-bone-100 rounded-br-none"
            : "bg-ink-700/70 border border-white/5 text-bone-100/95 rounded-bl-none"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start" data-testid="chat-typing">
      <div className="bg-ink-700/70 border border-white/5 rounded-lg rounded-bl-none px-4 py-3">
        <div className="flex gap-1.5 items-center">
          <span className="w-1.5 h-1.5 rounded-full bg-bone-200/60 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-bone-200/60 animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-bone-200/60 animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}
